import "./style.scss";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import DatabaseSvg from "@/src/svg/admissionOfficePage/DatabaseSvg";
import { reloadPageAfterNewDocumentConfirm } from "@/src/utils/admissionOffice/confirmNewDocumentReload";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import backForDegreeDocumentsPdf from "@/src/assets/admissionOfficePage/back-for-degree-documents.pdf";

interface IAOPConfig {
  setShowDegreePdf: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IAOPFormValues {
  fullname: string;
  whose_doc: string;
  date: string;
  document_seria: string;
  file: File | null;
}

const today = new Date();

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDate = (value: string) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
};

const AOPConfig: FC<IAOPConfig> = ({ setShowDegreePdf }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      file: null,
      date: formatDateForInput(today),
      document_seria: "",
      fullname: "",
      whose_doc: "",
    },
  });

  const dispatch = useDispatch();
  const [isGenerated, setIsGenerated] = useState(false);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);
  const selectedFile = watch("file");

  const generateNewDocument = async () => {
    const values = getValues();
    if (!values.file) {
      alert("Please upload a file");
      return;
    }

    dispatch(setIsPreloyder(true));
    try {
      const response = await admissionOfficeRequests.createSwitzerlandDegreeDoc({
        fullname: values.fullname,
        whose_doc: values.whose_doc,
        userOffice: "01",
        date: parseDate(values.date),
        document_seria: values.document_seria,
        file: values.file,
      });

      if (response) {
        setIsGenerated(true);
        setShowDegreePdf(true);
      }
    } catch {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  const handleCopy = async (fieldName: keyof IAOPFormValues) => {
    const value = getValues(fieldName);
    if (!value || typeof value !== "string") return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  const handleDownloadPdf = () => {
    const link = document.createElement("a");
    link.href = backForDegreeDocumentsPdf;
    link.download = "back-for-degree-documents.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const assignFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      alert("Only PDF files are allowed");
      return;
    }
    setValue("file", file, { shouldValidate: true });
    clearErrors("file");
  };

  return (
    <div className="admission-office-page-config">
      <div className="admission-office-page-config-logo">
        <div className="logo-svg">
          <LogoSvg />
        </div>
        <div className="logo-line"></div>
        <p>Platforma</p>
      </div>
      <p className="admission-office-page-config-title">Degree Documents</p>
      <form className="admission-office-page-config-form">
        <div className="form-group-fields">
          <div className="form-group-fields-input whose_doc">
            <AutocompleteInput
              placeholder="Whose"
              value={whoseDocAuto.query}
              onChange={whoseDocAuto.onChange}
              onSelect={(v) => {
                whoseDocAuto.onSelect(v);
                setValue("whose_doc", v);
              }}
              isOpen={whoseDocAuto.isOpen}
              options={whoseDocAuto.filtered}
              error={!!errors.whose_doc}
              required
              inputProps={{
                register: register("whose_doc", { required: true }),
                close: whoseDocAuto.close,
                setOpen: whoseDocAuto.onClick,
                onArrowClick: whoseDocAuto.onArrowClick,
              }}
              identifier={"whose"}
            />
          </div>
          <div className="form-group-fields-input date">
            <div className="form-group-fields-input-label">
              <label htmlFor="degree-date">
                Date of Issue<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="date" id="degree-date" type="date" {...register("date", { required: true })} />
            {errors.date && <p>This field is required</p>}
            <div className="form-group-fields-input-svg">
              <AODSvgExport.DateInputSvg />
            </div>
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="document-seria">
                Document Seria<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className="document-seria"
              id="document-seria"
              type="text"
              {...register("document_seria", { required: true })}
            />
            {errors.document_seria && <p>This field is required</p>}
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="degree-full-name">
                Full Name<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className="full-mame"
              id="degree-full-name"
              type="text"
              {...register("fullname", { required: true })}
            />
            {errors.fullname && <p>This field is required</p>}
            <div className="form-group-fields-input-svg">
              <div className="form-group-fields-input-svg-line"></div>
              <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullname")}>
                <AODSvgExport.FullNameCopySvg />
              </div>
            </div>
          </div>
        </div>

        <div className="form-group-file">
          <div className="form-group-file-title">
            <p>Files</p>
          </div>
          <div
            className="form-group-file-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              assignFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => document.getElementById("degree-file-input")?.click()}
          >
            <input
              id="degree-file-input"
              type="file"
              accept=".pdf,application/pdf"
              hidden
              onChange={(e) => {
                assignFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <div className="form-group-file-dropzone-placeholder">
              <p>Drag & Drop Files Here</p>
              <p>or</p>
              <span>Browse Files</span>
            </div>
          </div>
          {selectedFile?.name && (
            <div className="form-group-file-name">
              <p>
                {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </p>
              <button
                type="button"
                className="form-group-file-name-remove"
                onClick={() => setValue("file", null, { shouldValidate: true })}
                aria-label="Remove file"
              >
                <AODSvgExport.TrashSvg />
              </button>
            </div>
          )}
        </div>

        <div className="form-group-buttons">
          <div className="form-group-buttons-pack">
            <BtnDefault onClick={handleSubmit(generateNewDocument)}>
              <p>Generate</p>
              <AODSvgExport.GenerateSvg />
            </BtnDefault>
            <BtnDefault disabled={!isGenerated} onClick={handleDownloadPdf}>
              <p>Download PDF</p>
              <AODSvgExport.DownloadSvg />
            </BtnDefault>
            <Link to={"/admissions-office-database"}>
              <BtnDefault reverse={true}>
                <p>Database</p>
                <DatabaseSvg />
              </BtnDefault>
            </Link>
          </div>
          <div className="form-group-buttons-refresh" onClick={reloadPageAfterNewDocumentConfirm}>
            <BtnDefault onlyIcon={true}>
              <AODSvgExport.RefreshSvg />
            </BtnDefault>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AOPConfig;
